import type { Dict } from '@aviata/platform-types';

import { api } from '@/shared/api';

import type {
  AddToWatchlistPayload,
  WatchlistItem,
  WatchlistOfferRecommendation,
  WatchlistOffersResponse,
} from './types';

type WatchlistListResponse = WatchlistItem[];

type WatchlistEnvelope = {
  items?: WatchlistItem[];
  results?: WatchlistItem[];
  dict?: Dict.Entity;
};

export type WatchlistQueryData = {
  items: WatchlistItem[];
  dict?: Dict.Entity;
};

export type WatchlistOffersQueryData = {
  items: WatchlistOfferRecommendation[];
  dict?: Dict.Entity;
};

export const watchlistApi = {
  getWatchlist: (): Promise<WatchlistQueryData> =>
    api.client
      .get<WatchlistListResponse | WatchlistEnvelope>('/watchlist/')
      .then(response => {
        const { data } = response;
        if (Array.isArray(data)) {
          return { items: data };
        }
        if ('items' in data && data.items) {
          return { items: data.items, dict: data.dict };
        }
        if ('results' in data && data.results) {
          return { items: data.results, dict: data.dict };
        }
        return { items: [] };
      }),

  addToWatchlist: (payload: AddToWatchlistPayload): Promise<WatchlistItem> =>
    api.client.post('/watchlist/', payload).then(response => response.data),

  removeFromWatchlist: (id: string | number): Promise<void> =>
    api.client.delete(`/watchlist/${id}/`),

  /**
   * Fetches cheapest offers for a given departure city + a list of
   * destination directions (city names in any locale). Backend accepts
   * repeated `direction` params, e.g.
   * `/watchlist/offers/by-direction?departure=ALA&direction=Стамбул&direction=Москва`.
   *
   * Returns the offer items together with the response-level `dict` so
   * callers can resolve localized city / airline names without falling
   * back to the user's watchlist dictionary.
   */
  getWatchlistOffersByDirection: (
    departure: string,
    directions: string[],
  ): Promise<WatchlistOffersQueryData> => {
    if (!departure || directions.length === 0) {
      return Promise.resolve({ items: [] });
    }
    const params = new URLSearchParams();
    params.append('departure', departure);
    for (const direction of directions) {
      params.append('direction', direction);
    }
    const url = `/watchlist/offers/by-direction?${params.toString()}`;
    return api.client.get<WatchlistOffersResponse>(url).then(response => ({
      items: response.data.items ?? [],
      dict: response.data.dict,
    }));
  },
};
